import { Menu, MenuItem, PopoverProps, Typography } from '@mui/material';
import { Clock, Monitor } from 'lucide-react';
import { useMemo } from 'react';
import { TimeFormats } from 'src/graphql';
import { getDropdownMenuPaperSx } from 'src/utils/dropdown-paper';

const timeFormatOptions: {
  label: string;
  value: TimeFormats;
  selectedLabel: string;
  icon: React.ReactElement;
}[] = [
  {
    value: TimeFormats.TwelveHour,
    label: '12-hour',
    get selectedLabel() {
      return `${this.label} clock`;
    },
    icon: <Clock size={20} />,
  },
  {
    value: TimeFormats.TwentyFourHour,
    label: '24-hour',
    get selectedLabel() {
      return `${this.label} clock`;
    },
    icon: <Clock size={20} />,
  },
  {
    value: TimeFormats.Auto,
    selectedLabel: 'System default',
    label: 'Auto',
    icon: <Monitor size={20} />,
  },
];

export const useTimeFormatSwitch = ({
  isOpen,
  selectedFormat,
  onClose,
  onChange,
  anchorEl,
}: {
  isOpen: boolean;
  selectedFormat: TimeFormats;
  onClose?: () => void;
  onChange: (value: TimeFormats) => void;
  anchorEl: PopoverProps['anchorEl'];
}) => {
  const { selectedTimeFormatLabel, timeFormatsMenu } = useMemo(() => {
    return {
      selectedTimeFormatLabel: timeFormatOptions.find(
        (option) => option.value === selectedFormat,
      )?.selectedLabel,
      timeFormatsMenu: (
        <Menu
          anchorEl={anchorEl}
          open={isOpen}
          onClose={onClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          slotProps={{
            paper: {
              sx: (theme) => ({
                ...getDropdownMenuPaperSx(theme),
                minWidth: 200,
              }),
            },
          }}
        >
          {Object.entries(timeFormatOptions).map(([, option]) => (
            <MenuItem
              selected={option.value === selectedFormat}
              key={option.label}
              onClick={() => {
                onChange(option.value);
                onClose?.();
              }}
            >
              {option.icon}
              <Typography textAlign="center">{option.label}</Typography>
            </MenuItem>
          ))}
        </Menu>
      ),
    };
  }, [anchorEl, isOpen, onChange, onClose, selectedFormat]);

  return useMemo(
    () => ({
      timeFormatsMenu,
      selectedTimeFormatLabel,
    }),
    [timeFormatsMenu, selectedTimeFormatLabel],
  );
};
